import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { ReplenishmentResponse } from '@/features/decisions/decisions.api.types';
import { groupRows, type GroupedRow } from '@/features/decisions/utils/queueFormat';
import { useTenant } from '@/providers/TenantProvider';

export interface UseReplenishmentQueueParams {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  experimentIds: string[];
  period: string;
  pageSize?: number;
}

interface TimedResponse extends ReplenishmentResponse {
  __elapsed_ms: number;
}

// Owns the queue's paginated query, grouping, and page state.
// Components consume the returned shape and render — they don't call APIs.
export function useReplenishmentQueue({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentIds,
  period,
  pageSize = 10,
}: UseReplenishmentQueueParams) {
  const { tenantAlias } = useTenant();
  const [page, setPage] = useState(1);

  const query = useQuery<TimedResponse, Error>({
    queryKey: ['rep-queue', tenantAlias, runId, scenarioRunId, refExperimentId, experimentIds, period, page, pageSize],
    enabled: Boolean(runId && scenarioRunId && refExperimentId && experimentIds.length > 0 && period),
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const start = performance.now();
      const data = await apiFetch<ReplenishmentResponse>(tenantAlias, ApiEndpoint.SimulationReplenishment, {
        pathParams: { scenarioRunId, runId },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentIds,
          period,
          page,
          page_size: pageSize,
        },
      });
      return { ...data, __elapsed_ms: Math.round(performance.now() - start) };
    },
  });

  const primaryCompareId = experimentIds[0] ?? '';
  const groups = useMemo<GroupedRow[]>(
    () => groupRows(query.data?.rows ?? [], primaryCompareId),
    [query.data, primaryCompareId],
  );

  return {
    groups,
    primaryCompareId,
    resolved: query.data?.resolved,
    pageInfo: query.data?.page,
    elapsedMs: query.data?.__elapsed_ms,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    page,
    setPage,
    pageSize,
  };
}
